import ActionTypes from "../constants";
import GeneralUtils from "../utils/GeneralUtils";

/**
 * Actions related to managing mappings. This class interfaces with the API
 * to retrieve, create, update, and delete mapping data, dispatching actions to update the application state.
 *
 * @class
 * @example
 * const mappingActions = new MappingActions();
 * mappingActions.saveMapping({ /* mapping data *\/ });
 */
class MappingActions {
  /**
   * Saves a new mapping to the API and dispatches an action to store the new mapping data.
   *
   * @param {Object} mapping - The mapping object to be saved.
   * @returns {Promise} - A promise that resolves with the response of the save operation.
   */
  saveMapping(mapping) {
    return GeneralUtils.post({
      url: "mapping",
      object: mapping,
      actionType: ActionTypes.MAPPING_SAVED,
      successToast: "Mapping saved successfully",
      failToast: "Unable to save mapping, please try again",
    });
  }

  /**
   * Saves multiple mappings in bulk to the API and dispatches an action to store the new mapping data.
   *
   * @param {Object} mapping - The mapping object to be saved in bulk.
   * @returns {Promise} - A promise that resolves with the response of the save operation.
   */
  saveMappingBulk(mapping) {
    return GeneralUtils.post({
      url: "mapping/bulk",
      object: mapping,
      actionType: ActionTypes.MAPPING_BULK_SAVED,
      successToast: "Mapping bulk saved successfully",
      failToast: "Unable to save mapping bulk, please try again",
    });
  }

  /**
   * Generates a new mapping by sending the provided mapping object to the API and dispatches an action to store this data.
   *
   * @param {Object} mapping - The mapping object to be generated.
   * @returns {Promise} - A promise that resolves with the response of the generate operation.
   */
  generateMapping(mapping) {
    return GeneralUtils.post({
      url: "mapping/map",
      object: mapping,
      actionType: ActionTypes.MAPPING_GENERATED,
      successToast: "Mapping validated successfully",
      failToast: "Unable to generate the map, please try again",
    });
  }

  /**
   * Deletes a specific mapping by its ID from the API and dispatches an action to remove this data from the state.
   *
   * @param {string} mappingId - The unique identifier of the mapping to be deleted.
   * @returns {Promise} - A promise that resolves with the response of the delete operation.
   */
  deleteMapping(mappingId) {
    return GeneralUtils.delete({
      url: `mapping/${mappingId}`,
      actionType: ActionTypes.MAPPING_DELETED,
      successToast: "Mapping deleted successfully",
      failToast: "Unable to delete mapping, please try again",
    });
  }

  /**
   * Fetches mappings for a specific data source from the API and dispatches an action to store this data.
   *
   * @param {string} dataSourceId - The unique identifier of the data source.
   * @returns {Promise} - A promise that resolves with the fetched data.
   */
  getMappingByDataSource(dataSourceId) {
    return GeneralUtils.get({
      url: `mapping/data-source/${dataSourceId}`,
      actionType: ActionTypes.MAPPING_BY_DATA_SOURCE_FETCHED,
    });
  }

  /**
   * Fetches mappings for a specific entity type from the API and dispatches an action to store this data.
   *
   * @param {string} entityTypeId - The unique identifier of the entity type.
   * @returns {Promise} - A promise that resolves with the fetched data.
   */
  getMappingByEntity(entityTypeId) {
    return GeneralUtils.get({
      url: `mapping/entity-type/${entityTypeId}`,
      actionType: ActionTypes.MAPPING_BY_ENTITY_FETCHED,
    });
  }
}

const mappingActions = new MappingActions();
export default mappingActions;
